// src/components/popups/RequestCallbackModal.js
import React, { useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import InputWithLabel from "../Form/Input/InputWithLabel";
import api from '../../api'; // Assuming api setup is correct
import { useUser } from '../../context/UserContext'; // To pre-fill data

export default function RequestCallbackModal({ show, recipient, recipientType, onClose }) {
    const { user, isAuthenticated } = useUser();
    const [formData, setFormData] = useState({
        name: '',
        phone_number: '',
    });
    const [errors, setErrors] = useState({});
    const [requestSent, setRequestSent] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false); // Prevent double submission
    const [submitError, setSubmitError] = useState(null); // For general submission errors

    // Pre-fill form if user is logged in
    useEffect(() => {
        if (show && isAuthenticated && user) {
            setFormData(prevData => ({
                ...prevData,
                // Use more specific user fields if available
                name: user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || '',
                phone_number: user.phone_number || user.phone || '',
            }));
            // Reset status on modal open
            setRequestSent(false);
            setErrors({});
            setSubmitError(null);
        } else if (show) {
             // Reset form for anonymous users when modal opens
             setFormData({ name: '', phone_number: '' });
             setRequestSent(false);
             setErrors({});
             setSubmitError(null);
        }
    }, [show, isAuthenticated, user]); // Rerun when modal `show` changes

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }));
        // Clear specific field error on change
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
        setSubmitError(null); // Clear general submit error on change
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }
        if (formData.phone_number.trim() && !/^\+?[0-9\s\-()]{7,}$/.test(formData.phone_number)) {
            newErrors.phone_number = 'Please enter a valid phone number';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError(null); // Clear previous errors

        if (!validateForm() || isSubmitting) {
            return;
        }

        setIsSubmitting(true);

        const dataToSend = {
            ...formData,
            recipient_id: recipientType === 'attorney' ? recipient.attorney_id : recipient.id,
            recipient_type: recipientType,
        };

        console.log('Submitting Callback Request:', dataToSend);

        try {
            // Use the new backend endpoint
            const response = await api.post('/request-callback/', dataToSend);

            console.log('Response:', response.data); // Debug log

            // Check for successful status (adjust based on backend response)
            if (response.status === 201 || response.data?.status?.startsWith('callback_requested')) {
                setRequestSent(true);
                // Optionally reset form fields here if modal stays open
                // setFormData({ name: '', phone_number: '' });
            } else {
                 // Handle potential non-201 success statuses if backend returns them
                 setSubmitError(response.data?.message || 'An unexpected response occurred.');
            }
        } catch (error) {
            console.error('Error requesting callback:', error.response ? error.response.data : error);
             const errorMsg = error.response?.data?.message || 'Failed to submit request. Please try again later.';
             setSubmitError(errorMsg);
             // Optionally set field-specific errors if backend provides them
             if (error.response?.data?.errors) {
                 setErrors(prev => ({ ...prev, ...error.response.data.errors }));
             }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Use useCallback for the close handler passed to the overlay/button
    const handleClose = useCallback(() => {
        if (!isSubmitting) { // Prevent closing while submitting
            onClose();
        }
    }, [onClose, isSubmitting]);

    // Reset state when the modal is hidden (prop `show` becomes false)
     useEffect(() => {
         if (!show) {
             // Delay reset slightly to allow fade-out animation if any
             const timer = setTimeout(() => {
                 setFormData({ name: '', phone_number: '' });
                 setErrors({});
                 setRequestSent(false);
                 setIsSubmitting(false);
                 setSubmitError(null);
             }, 300); // Adjust timing if needed
             return () => clearTimeout(timer);
         }
     }, [show]);


    // Render nothing if show is false
    if (!show) {
        return null;
    }

    // Use portal to render modal at the body level
    return ReactDOM.createPortal(
        <div className={`modal ${show ? 'visible' : ''}`}>
            <div className="modal__overlay" onClick={handleClose}></div>
            <div className="modal__content">
                <button onClick={handleClose} className="button no-style close js--close-modal" aria-label="Close modal" disabled={isSubmitting}>
                    <img src="/assets/icon--close-CN6QlgAA.svg" alt=""/>
                </button>
                <div className="modal__form">
                    {requestSent ? (
                        <div className="content text-center"> {/* Added text-center */}
                            {/* You might want a more specific success icon */}
                            <img src="/assets/icon--sendok.svg" alt="Success" style={{ width: '80px', marginBottom: '20px' }}/>
                            <h2>Callback Requested</h2>
                            <p>Your request has been sent successfully. They will contact you soon.</p>
                            <button type='button' onClick={handleClose} className="button button-black">
                                Okay
                            </button>
                        </div>
                    ) : (
                        <form className="content" onSubmit={handleSubmit} noValidate>
                            <h2>Request a Callback</h2>
                            <p>Please provide your name and phone number, and {recipient?.name || 'they'} will call you back.</p>

                            {submitError && (
                                <div className="error-message general-error">{submitError}</div>
                            )}

                            <InputWithLabel
                                name="name"
                                label="Your Name*"
                                value={formData.name}
                                onChange={handleChange}
                                error={errors.name}
                                // Disable if logged in and name is pre-filled (optional)
                                // disabled={isAuthenticated && user?.name}
                            />
                            <InputWithLabel
                                name="phone_number"
                                label="Your Phone Number (Optional)"
                                type="tel" // Use tel type for better mobile UX
                                value={formData.phone_number}
                                onChange={handleChange}
                                error={errors.phone_number}
                                // Disable if logged in and phone is pre-filled (optional)
                                // disabled={isAuthenticated && (user?.phone_number || user?.phone)}
                                required // HTML5 validation
                            />

                            <button type="submit" className="button button-black" disabled={isSubmitting}>
                                {isSubmitting ? 'Submitting...' : 'Request Callback'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>,
        document.body // Portal target
    );
}